import { Button, Input, Switch, Table, Tooltip } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import deleteWhiteIcon from "../../../assets/images/icon/deleteWhiteIcon.png";
import EditIcon from "../../../assets/images/edit.svg";
import Plus from "../../../assets/images/plus.svg";
import DeleteModal from "../../../components/DeleteModal";
import SectionWrapper from "../../../components/SectionWrapper";
import ShowTotal from "../../../components/ShowTotal";
import { AppStateContext } from "../../../context/AppContext";
import apiPath from "../../../constants/apiPath";
import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useDebounce from "../../../hooks/useDebounce";
import useRequest from "../../../hooks/useRequest";
import AddForm from "./AddForm";

const normalizeOptions = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => ({
      value: item?._id || item?.id || item?.value || item,
      label: item?.name || item?.title || item?.slug || item?.value || item,
      raw: item,
    }))
    .filter((item) => item.value);

function Index() {
  const heading = "Variants Management";
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const requestRef = useRef(request);

  const api = {
    addEdit: apiPath.variant,
    list: apiPath.variant,
    products: apiPath.product,
  };

  const [searchText, setSearchText] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [deleteModal, showDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const debouncedSearchText = useDebounce(searchText, 300);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const fetchProducts = useCallback(() => {
    requestRef.current({
      url: `${api.products}?page=1&pageSize=100`,
      method: "GET",
      onSuccess: ({ data }) => {
        setProductOptions(normalizeOptions(data?.docs || data));
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to load products", Severty.ERROR);
      },
    });
  }, [api.products]);

  const fetchData = useCallback(
    (pageConfig = { current: 1, pageSize: 10 }) => {
      setLoading(true);

      requestRef.current({
        url: `${api.list}?page=${pageConfig?.current || 1}&pageSize=${pageConfig?.pageSize || 10}&search=${debouncedSearchText}`,
        method: "GET",
        onSuccess: ({ data }) => {
          setLoading(false);
          setList(
            (data?.docs || []).map((item) => ({
              ...item,
              key: item._id,
            })),
          );
          setPagination((prev) => ({
            ...prev,
            current: pageConfig?.current || 1,
            pageSize: pageConfig?.pageSize || 10,
            total: data?.totalDocs || 0,
          }));
        },
        onError: (error) => {
          setLoading(false);
          ShowToast(error?.response?.data?.message || error?.message || "Failed to load variants", Severty.ERROR);
        },
      });
    },
    [api.list, debouncedSearchText],
  );

  useEffect(() => {
    setPageHeading(heading);
    fetchProducts();
  }, [fetchProducts, heading, setPageHeading]);

  useEffect(() => {
    fetchData({ current: 1, pageSize: pagination.pageSize });
  }, [debouncedSearchText, fetchData, pagination.pageSize, refresh]);

  const handleChangeStatus = (record) => {
    requestRef.current({
      url: `${api.addEdit}/${record?._id}`,
      method: "PUT",
      data: {
        title: record?.title || "",
        description: record?.description || "",
        product_id: record?.product_id?._id || record?.product_id || "",
        price: record?.price ?? null,
        stock: record?.stock ?? null,
        stock_keeping_unit: record?.stock_keeping_unit || "",
        is_active: !record?.is_active,
      },
      onSuccess: (response) => {
        ShowToast(response.message, Severty.SUCCESS);
        setRefresh((prev) => !prev);
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to update status", Severty.ERROR);
      },
    });
  };

  const onDelete = (id) => {
    requestRef.current({
      url: `${api.addEdit}/${id}`,
      method: "DELETE",
      onSuccess: (response) => {
        ShowToast(response.message, Severty.SUCCESS);
        setRefresh((prev) => !prev);
      },
      onError: (error) => {
        ShowToast(error?.response?.data?.message || error?.message || "Failed to delete variant", Severty.ERROR);
      },
    });
  };

  const columns = [
    {
      title: lang("Title"),
      dataIndex: "title",
      key: "title",
      render: (_, { title }) => <span className="cap">{title || "-"}</span>,
    },
    {
      title: lang("Product"),
      dataIndex: "product_id",
      key: "product_id",
      render: (_, { product_id }) => <span className="cap">{product_id?.title || product_id?.name || product_id || "-"}</span>,
    },
    {
      title: lang("SKU"),
      dataIndex: "stock_keeping_unit",
      key: "stock_keeping_unit",
      render: (_, { stock_keeping_unit }) => <span className="cap">{stock_keeping_unit || "-"}</span>,
    },
    {
      title: lang("Price"),
      dataIndex: "price",
      key: "price",
      render: (_, { price }) => <span>{price ?? "-"}</span>,
    },
    {
      title: lang("Stock"),
      dataIndex: "stock",
      key: "stock",
      render: (_, { stock }) => <span>{stock ?? "-"}</span>,
    },
    {
      title: lang("Status"),
      key: "is_active",
      dataIndex: "is_active",
      render: (_, record) => <Switch checked={record?.is_active} onChange={() => handleChangeStatus(record)} />,
    },
    {
      title: lang("Action"),
      key: "action",
      render: (_, record) => (
        <div className="d-flex justify-contenbt-start">
          <Tooltip title={lang("Edit")} color={"purple"} key={`edit-${record?._id}`}>
            <Button
              title={lang("Edit")}
              className="edit-cls btnStyle primary_btn"
              onClick={() => {
                setSelected(record);
                setVisible(true);
              }}
            >
              <img src={EditIcon} alt="" />
            </Button>
          </Tooltip>

          <Tooltip title={lang("Delete")} color={"purple"} key={`delete-${record?._id}`}>
            <Button
              title={lang("Delete")}
              className="btnStyle deleteDangerbtn"
              onClick={() => {
                setSelected(record);
                showDeleteModal(true);
              }}
            >
              <img src={deleteWhiteIcon} alt="" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <SectionWrapper
        cardHeading={heading}
        extra={
          <div className="w-100 text-head_right_cont flex-wrap gap-2">
            <div className="pageHeadingSearch">
              <Input.Search className="searchInput" placeholder={lang("Search by title, SKU or description")} onChange={(e) => setSearchText(e.target.value)} allowClear />
            </div>

            <Button
              className="primary_btn btnStyle"
              onClick={() => {
                setSelected(undefined);
                setVisible(true);
              }}
            >
              <span className="add-Ic">
                <img src={Plus} alt="" />
              </span>
              {lang("Add New Variant")}
            </Button>
          </div>
        }
      >
        <h4 className="text-right mb-1cont-space cont-space">{ShowTotal(pagination.total || 0)}</h4>
        <div className="table-responsive customPagination checkBoxSrNo">
          <Table
            loading={loading}
            columns={columns}
            dataSource={list}
            pagination={{
              current: pagination.current,
              defaultPageSize: 10,
              responsive: true,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSize: pagination.pageSize,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            onChange={(nextPagination) => fetchData(nextPagination)}
            className="ant-border-space keyword-emotion-table"
          />
        </div>
      </SectionWrapper>

      {visible && (
        <AddForm
          api={api}
          show={visible}
          hide={() => {
            setSelected(undefined);
            setVisible(false);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
          productOptions={productOptions}
        />
      )}

      {deleteModal && (
        <DeleteModal
          reasons={[]}
          title={lang("Delete Variant")}
          subtitle={lang("This action will permanently remove the record. Are you sure?")}
          show={deleteModal}
          hide={() => {
            showDeleteModal(false);
            setSelected(undefined);
          }}
          onOk={() => onDelete(selected?._id)}
        />
      )}
    </>
  );
}

export default Index;
